/**
 * 案件データ型変換ユーティリティ
 *
 * CompanyCase型とCase型の間の変換を提供します。
 * FL-162でデータ取得を`company_cases`テーブルに統一した際の
 * 後方互換性を維持するための変換関数を集約しています。
 */

import type { CompanyCase, Case } from '../types';

/**
 * CompanyCase型をCase型に変換
 *
 * @param companyCase - company_casesテーブルから取得したデータ
 * @returns Case型に変換されたデータ（後方互換性のため）
 *
 * @remarks
 * - FL-162でデータ取得をcompany_casesテーブルに統一
 * - 既存のUIコンポーネントはCase型を期待しているため、変換が必要
 * - 将来的にCase型を廃止し、CompanyCase型に統一する予定
 */
export function convertCompanyCaseToCase(companyCase: CompanyCase): Case {
  const result: Case = {
    id: companyCase.id,
    caseNumber: companyCase.caseNumber,
    status: companyCase.status as Case['status'],

    // FL-167対応: assignedLandTransporterIdをデータベースに正確に保存
    // transporter_idカラムは削除済み（マイグレーション: 20251018224644）
    assignedLandTransporterId: companyCase.assignedLandTransporterId || null,

    // 取引先情報
    billingPartnerId: companyCase.billingPartnerId || '',
    billingLocationId: companyCase.billingLocationId,
    pickupPartnerId: companyCase.pickupPartnerId || '',
    pickupLocationId: companyCase.pickupLocationId,
    deliveryPartnerId: companyCase.deliveryPartnerId || '',
    deliveryLocationId: companyCase.deliveryLocationId,

    // Legacy Client IDs（空文字列でデフォルト）
    billingClientId: '',
    pickupClientId: '',
    deliveryClientId: '',

    // 住所（自由入力）
    billingAddressCustom: companyCase.billingAddressCustom,
    pickupAddressCustom: companyCase.pickupAddressCustom,
    deliveryAddressCustom: companyCase.deliveryAddressCustom,

    // 引き取り情報
    pickupAddress: companyCase.pickupAddressCustom || '',
    pickupPhone: companyCase.pickupPhone || '',
    pickupDate: companyCase.pickupDate,
    pickupTimeStart: companyCase.pickupTimeStart || '',
    pickupTimeEnd: companyCase.pickupTimeEnd || '',
    pickupDateEnd: companyCase.pickupDateEnd,
    pickupReport: companyCase.pickupReport || null,

    // 搬入情報
    deliveryAddress: companyCase.deliveryAddressCustom || '',
    deliveryPhone: companyCase.deliveryPhone || '',
    deliveryDate: companyCase.deliveryDate,
    deliveryTime: companyCase.deliveryTime || '',
    deliveryDateEnd: companyCase.deliveryDateEnd,
    deliveryReport: companyCase.deliveryReport || null,

    // 車両情報
    vehicleModel: companyCase.vehicleModel || '',
    vehicleModelId: companyCase.vehicleModelId,
    vehicleDisplayName: companyCase.vehicleDisplayName,
    vehicleType: '', // CompanyCaseには存在しないため空文字列
    vehiclePlateNumber: companyCase.vehiclePlateNumber || '',
    vehiclePlateNumberNone: companyCase.vehiclePlateNumberNone,
    vehicleNumber: companyCase.vehicleNumber, // FL-188: 車体番号（VIN）
    chassisNumber: companyCase.chassisNumber, // FL-191: 車体番号

    // FL-113: 車検状態管理
    isVehicleInspectionExpired: companyCase.isVehicleInspectionExpired,
    requiresTemporaryNumber: companyCase.requiresTemporaryNumber,
    temporaryNumberNotes: companyCase.temporaryNumberNotes,

    // FL-89: 新規フィールド
    requester: companyCase.requester,
    managementNumber: companyCase.managementNumber,
    transportFee: companyCase.transportFee,

    // 請求
    price: companyCase.price || 0,

    // その他
    remarks: companyCase.remarks || '',
  };

  return result;
}

/**
 * CompanyCase配列をCase配列に一括変換
 *
 * @param companyCases - company_casesテーブルから取得したデータ配列
 * @returns Case型の配列
 */
export function convertCompanyCasesToCases(companyCases: CompanyCase[]): Case[] {
  return companyCases.map(convertCompanyCaseToCase);
}
